(() => {
  if (window.ReeflineProduct) {
    window.ReeflineProduct.initialize(document);
    return;
  }

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const productVideoAllowed = () => (
    !reducedMotion.matches
    && document.documentElement.dataset.motion !== 'off'
    && !navigator.connection?.saveData
  );

  class ProductHeroVideo {
    constructor(root) {
      this.media = root.querySelector('[data-product-hero-media]');
      this.video = this.media?.querySelector('[data-product-page-video]');
      this.active = this.media?.classList.contains('is-active') || false;
      this.pauseTimer = null;
      this.idleHandle = null;
      this.resumeWhenVisible = false;
      if (!this.media || !this.video) return;
      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerOut = this.onPointerOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPolicyChange = this.onPolicyChange.bind(this);
      this.media.addEventListener('pointerover', this.onPointerOver, { passive: true });
      this.media.addEventListener('pointerout', this.onPointerOut, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      reducedMotion.addEventListener?.('change', this.onPolicyChange);
      navigator.connection?.addEventListener?.('change', this.onPolicyChange);
      if (document.readyState === 'complete') this.scheduleLoad();
      else window.addEventListener('load', this.onWindowLoad, { once: true });
    }

    onWindowLoad() {
      this.scheduleLoad();
    }

    scheduleLoad() {
      if (!this.video || !this.active || !productVideoAllowed() || document.hidden || this.idleHandle !== null) return;
      const start = () => {
        this.idleHandle = null;
        if (this.active && productVideoAllowed() && !document.hidden) this.play();
      };
      if ('requestIdleCallback' in window) this.idleHandle = window.requestIdleCallback(start, { timeout: 1200 });
      else this.idleHandle = window.setTimeout(start, 0);
    }

    attachSources() {
      let attached = false;
      this.video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        attached = true;
      });
      if (attached || this.video.readyState === 0) this.video.load();
    }

    play() {
      if (!this.video || !this.active || !productVideoAllowed() || document.hidden) return;
      this.attachSources();
      this.video.play().then(() => {
        if (!this.active || !productVideoAllowed() || document.hidden) {
          this.video.pause();
          return;
        }
        this.media.classList.add('is-product-video-playing');
      }).catch(() => {
        this.media.classList.remove('is-product-video-playing', 'is-product-video-pausing');
      });
    }

    onPointerOver(event) {
      if (!finePointer.matches || !this.media.classList.contains('is-product-video-playing') || this.media.contains(event.relatedTarget)) return;
      window.clearTimeout(this.pauseTimer);
      this.media.classList.add('is-product-video-pausing');
      this.pauseTimer = window.setTimeout(() => {
        this.video.pause();
        this.pauseTimer = null;
      }, 160);
    }

    onPointerOut(event) {
      if (!finePointer.matches || this.media.contains(event.relatedTarget)) return;
      window.clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
      this.media.classList.remove('is-product-video-pausing');
      this.play();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.resumeWhenVisible = !this.video.paused && this.active;
        this.video.pause();
      } else if (this.resumeWhenVisible) {
        this.resumeWhenVisible = false;
        this.play();
      }
    }

    onPolicyChange() {
      if (!productVideoAllowed()) {
        window.clearTimeout(this.pauseTimer);
        this.video.pause();
        this.media.classList.remove('is-product-video-playing', 'is-product-video-pausing');
        return;
      }
      this.scheduleLoad();
    }

    setActive(active) {
      if (!this.video) return;
      this.active = active;
      if (!active) {
        window.clearTimeout(this.pauseTimer);
        this.video.pause();
        this.media.classList.remove('is-product-video-playing', 'is-product-video-pausing');
        return;
      }
      this.scheduleLoad();
    }

    destroy() {
      window.clearTimeout(this.pauseTimer);
      if (this.idleHandle !== null) {
        if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.idleHandle);
        else window.clearTimeout(this.idleHandle);
      }
      window.removeEventListener('load', this.onWindowLoad);
      this.media?.removeEventListener('pointerover', this.onPointerOver);
      this.media?.removeEventListener('pointerout', this.onPointerOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      reducedMotion.removeEventListener?.('change', this.onPolicyChange);
      navigator.connection?.removeEventListener?.('change', this.onPolicyChange);
      this.video?.pause();
    }
  }

  // Floating buy pill: appears once the buybox button has scrolled above the
  // viewport, hides again when it returns. The pill submits the real product
  // form through its form attribute, so the existing add flow owns the request.
  class ProductFloat {
    constructor(root) {
      this.float = root.closest?.('.shopify-section')?.querySelector?.('[data-product-float]') || root.querySelector?.('[data-product-float]') || null;
      this.anchor = root.querySelector('[data-add-to-cart]');
      this.hideTimer = null;
      if (!this.float || !this.anchor || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.toggle(passed);
      }, { threshold: 0 });
      this.observer.observe(this.anchor);
    }

    toggle(show) {
      if (!this.float) return;
      window.clearTimeout(this.hideTimer);
      if (show) {
        this.float.hidden = false;
        window.requestAnimationFrame(() => {
          if (!this.float.hidden) this.float.classList.add('is-visible');
        });
        return;
      }
      this.float.classList.remove('is-visible');
      this.hideTimer = window.setTimeout(() => {
        if (!this.float.classList.contains('is-visible')) this.float.hidden = true;
      }, reducedMotion.matches ? 0 : 320);
    }

    destroy() {
      window.clearTimeout(this.hideTimer);
      this.observer?.disconnect();
      if (this.float) {
        this.float.classList.remove('is-visible');
        this.float.hidden = true;
      }
    }
  }

  class ProductInfo {
    constructor(root) {
      this.root = root;
      this.request = null;
      this.mediaFrame = null;
      this.mediaQuery = window.matchMedia('(max-width: 960px)');
      this.mediaTrack = root.querySelector('[data-media-track]');
      this.heroVideo = new ProductHeroVideo(root);
      this.float = new ProductFloat(root);
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onMediaScroll = this.onMediaScroll.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      root.addEventListener('change', this.onChange);
      root.addEventListener('click', this.onClick);
      root.addEventListener('submit', this.onSubmit);
      this.mediaTrack?.addEventListener('scroll', this.onMediaScroll, { passive: true });
      this.mediaQuery.addEventListener?.('change', this.onViewportChange);
      const selectedId = root.querySelector('[data-variant-id]')?.value;
      root.querySelectorAll('[data-variant-id], [data-installment-variant-id]').forEach((input) => { input.disabled = !selectedId; });
      this.syncMedia(this.activeMedia()?.dataset.mediaId, { scroll: false });
    }

    mediaItems() {
      return Array.from(this.root.querySelectorAll('[data-media-id]'));
    }

    activeMedia() {
      return this.root.querySelector('[data-media-id].is-active') || this.mediaItems()[0];
    }

    optionValueIds() {
      return Array.from(this.root.querySelectorAll('[data-option-position]')).map((group) => {
        const input = group.querySelector('[data-option-input]:checked') || group.querySelector('select[data-option-input]');
        if (!input) return null;
        if (input.matches('select')) return input.selectedOptions[0]?.dataset.optionValueId;
        return input.dataset.optionValueId;
      }).filter(Boolean);
    }

    onChange(event) {
      if (!event.target.matches('[data-option-input]')) return;
      const selectedOption = event.target.matches('select') ? event.target.selectedOptions[0] : event.target;
      this.refreshProductMarkup({
        productUrl: selectedOption?.dataset.productUrl || this.root.dataset.productUrl,
        optionValueIds: this.optionValueIds(),
        focusId: event.target.id,
        optionLabel: selectedOption?.textContent?.trim() || selectedOption?.value
      });
    }

    onClick(event) {
      const control = event.target.closest('[data-quantity-plus], [data-quantity-minus], [data-media-target], [data-media-previous], [data-media-next]');
      if (!control) return;
      if (control.matches('[data-media-target]')) {
        this.showMedia(control.dataset.mediaTarget);
        return;
      }
      if (control.matches('[data-media-previous], [data-media-next]')) {
        const items = this.mediaItems();
        const currentIndex = Math.max(0, items.indexOf(this.activeMedia()));
        const offset = control.hasAttribute('data-media-next') ? 1 : -1;
        const target = items[Math.min(items.length - 1, Math.max(0, currentIndex + offset))];
        if (target) this.showMedia(target.dataset.mediaId);
        return;
      }
      const input = control.closest('[data-quantity]')?.querySelector('input');
      if (!input) return;
      const next = Math.max(Number(input.min || 1), Number(input.value || 1) + (control.hasAttribute('data-quantity-plus') ? 1 : -1));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onSubmit(event) {
      const form = event.target.closest('[data-product-form]');
      if (!form || !this.root.contains(form) || !window.ReeflineCart?.add || !window.fetch || !window.FormData) return;
      event.preventDefault();
      const submit = form.querySelector('[data-add-to-cart]');
      const error = this.root.querySelector('[data-product-error]');
      window.ReeflineCart.add(form, { launcher: submit, errorTarget: error });
    }

    showMedia(mediaId) {
      const target = this.mediaItems().find((media) => String(media.dataset.mediaId) === String(mediaId));
      if (!target) return;
      this.syncMedia(mediaId, { scroll: this.mediaQuery.matches });
    }

    syncMedia(mediaId, { scroll = false } = {}) {
      const items = this.mediaItems();
      const target = items.find((media) => String(media.dataset.mediaId) === String(mediaId)) || items[0];
      if (!target) return;
      const activeIndex = items.indexOf(target);
      items.forEach((media, index) => {
        const active = index === activeIndex;
        media.classList.toggle('is-active', active);
        media.setAttribute('aria-current', active ? 'true' : 'false');
        if (!active) {
          media.querySelectorAll('video').forEach((video) => video.pause());
          media.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
          media.querySelectorAll('iframe').forEach((frame) => {
            const source = frame.getAttribute('src') || '';
            if (/youtu(?:\.be|be\.com)/i.test(source)) {
              frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
            } else if (/vimeo\.com/i.test(source)) {
              frame.contentWindow?.postMessage({ method: 'pause' }, '*');
            }
          });
        }
      });
      this.root.querySelectorAll('[data-media-target]').forEach((thumb) => {
        const active = String(thumb.dataset.mediaTarget) === String(target.dataset.mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
      const counter = this.root.querySelector('[data-media-counter]');
      if (counter) {
        const current = activeIndex + 1;
        counter.textContent = `${current} / ${items.length}`;
        counter.setAttribute('aria-label', (counter.dataset.counterLabel || 'Media __CURRENT__').replace('__CURRENT__', String(current)));
      }
      const previous = this.root.querySelector('[data-media-previous]');
      const next = this.root.querySelector('[data-media-next]');
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === items.length - 1;
      // Desktop shows every tile at once, so the hero video is active whenever the grid is; on the mobile carousel only while it is the current slide.
      this.heroVideo.setActive(!this.mediaQuery.matches || target === this.heroVideo.media);
      if (scroll && this.mediaTrack) {
        target.scrollIntoView({
          block: 'nearest',
          inline: 'start',
          behavior: reducedMotion.matches || document.documentElement.dataset.motion === 'off' ? 'auto' : 'smooth'
        });
      }
    }

    onMediaScroll() {
      if (!this.mediaQuery.matches || !this.mediaTrack) return;
      window.cancelAnimationFrame(this.mediaFrame);
      this.mediaFrame = window.requestAnimationFrame(() => {
        const items = this.mediaItems();
        if (!items.length) return;
        const trackLeft = this.mediaTrack.getBoundingClientRect().left;
        const target = items.reduce((closest, item) => (
          Math.abs(item.getBoundingClientRect().left - trackLeft) < Math.abs(closest.getBoundingClientRect().left - trackLeft) ? item : closest
        ));
        this.syncMedia(target.dataset.mediaId, { scroll: false });
      });
    }

    onViewportChange() {
      const active = this.activeMedia();
      if (!this.mediaQuery.matches && this.mediaTrack) this.mediaTrack.scrollLeft = 0;
      this.syncMedia(active?.dataset.mediaId, { scroll: false });
    }

    async refreshProductMarkup({ productUrl, optionValueIds, focusId, optionLabel }) {
      this.request?.abort();
      this.request = new AbortController();
      const request = this.request;
      this.root.setAttribute('aria-busy', 'true');
      const url = new URL(productUrl || this.root.dataset.productUrl, window.location.origin);
      url.searchParams.set('section_id', this.root.dataset.sectionId);
      if (optionValueIds.length) url.searchParams.set('option_values', optionValueIds.join(','));
      try {
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: request.signal });
        if (!response.ok) throw new Error(window.themeStrings?.unavailable);
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const freshRoot = documentFragment.querySelector('[data-product-root]');
        if (!freshRoot) throw new Error(window.themeStrings?.unavailable);
        const parent = this.root.parentElement;
        const selectedVariantId = freshRoot.querySelector('[data-variant-id]')?.value;
        const price = freshRoot.querySelector('.product-detail__block--price .price, .product-detail__price .price')?.textContent?.replace(/\s+/g, ' ').trim() || '';
        const addButton = freshRoot.querySelector('[data-add-to-cart]');
        const availability = addButton?.dataset.labelDefault || window.themeStrings?.unavailable || '';
        const shouldUpdateUrl = this.root.dataset.updateUrl === 'true';
        window.Reefline?.destroy?.(this.root);
        this.destroy();
        freshRoot.removeAttribute('aria-busy');
        this.root.replaceWith(freshRoot);
        initialize(parent || document);
        window.Reefline?.initialize?.(freshRoot);
        document.getElementById(focusId)?.focus({ preventScroll: true });
        if (shouldUpdateUrl) {
          const browserUrl = new URL(url.pathname, window.location.origin);
          if (selectedVariantId) browserUrl.searchParams.set('variant', selectedVariantId);
          window.history.replaceState({}, '', browserUrl);
        }
        const template = window.themeStrings?.selectionStatus;
        const message = template
          ? template.replace('__OPTION__', optionLabel || '').replace('__PRICE__', price).replace('__AVAILABILITY__', availability)
          : [optionLabel, price, availability].filter(Boolean).join('. ');
        window.Reefline?.announce?.(message);
      } catch (error) {
        if (error.name !== 'AbortError') window.Reefline?.announce?.(error.message || window.themeStrings?.unavailable);
      } finally {
        if (this.request === request && this.root.isConnected) this.root.removeAttribute('aria-busy');
      }
    }

    destroy() {
      this.request?.abort();
      window.cancelAnimationFrame(this.mediaFrame);
      this.heroVideo.destroy();
      this.float?.destroy();
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('submit', this.onSubmit);
      this.mediaTrack?.removeEventListener('scroll', this.onMediaScroll);
      this.mediaQuery.removeEventListener?.('change', this.onViewportChange);
    }
  }

  class Recommendations {
    constructor(element) {
      this.element = element;
      if (!element.dataset.url || element.children.length) return;
      this.load();
    }
    async load() {
      try {
        const response = await fetch(this.element.dataset.url);
        if (!response.ok) return;
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = parsed.querySelector('[data-product-recommendations]');
        if (fresh?.children.length) this.element.replaceWith(fresh);
      } catch (_) {
        // Recommendations are optional and stay hidden when Shopify returns no products.
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-product-root]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new ProductInfo(root));
    });
    scope.querySelectorAll('[data-product-recommendations]').forEach((node) => {
      if (instances.has(node)) return;
      instances.set(node, new Recommendations(node));
    });
  };

  const destroy = (scope) => scope.querySelectorAll('[data-product-root], [data-product-recommendations]').forEach((node) => {
    instances.get(node)?.destroy?.();
    instances.delete(node);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  window.ReeflineProduct = { initialize, destroy };
})();
